/**
 * Session Manager - Handles session security and activity tracking
 */

// Session timeout in milliseconds (30 minutes of inactivity for normal sessions)
const SESSION_TIMEOUT = 30 * 60 * 1000;

// Maximum session duration (12 hours for normal sessions)
const MAX_SESSION_DURATION = 12 * 60 * 60 * 1000;

// Remember me session timeout (7 days of inactivity)
const REMEMBER_ME_TIMEOUT = 7 * 24 * 60 * 60 * 1000;

// Remember me maximum session duration (30 days)
const REMEMBER_ME_MAX_DURATION = 30 * 24 * 60 * 60 * 1000;

/**
 * Check if remember me is enabled
 */
const isRememberMeEnabled = () => {
  return localStorage.getItem('remember_me') === 'true';
};

/**
 * Update last activity timestamp
 */
export const updateActivity = () => {
  const storage = isRememberMeEnabled() ? localStorage : sessionStorage;
  storage.setItem('last_activity', Date.now().toString());
};

/**
 * Check if session is valid
 * @returns {Object} { valid: boolean, reason: string }
 */
export const checkSession = () => {
  const rememberMe = isRememberMeEnabled();
  const storage = rememberMe ? localStorage : sessionStorage;

  const sessionStart = storage.getItem('session_start');
  const lastActivity = storage.getItem('last_activity');

  if (!sessionStart || !lastActivity) {
    return { valid: false, reason: 'no_session' };
  }

  const now = Date.now();
  const inactiveDuration = now - parseInt(lastActivity);

  // For remember me sessions, check against the stored expiry
  if (rememberMe) {
    const sessionExpiry = localStorage.getItem('session_expiry');

    if (!sessionExpiry) {
      return { valid: false, reason: 'no_expiry' };
    }

    // Check if 30-day session has expired
    if (parseInt(sessionExpiry) < now) {
      return { valid: false, reason: 'session_expired' };
    }

    // Check if user has been inactive too long (7 days for remember me)
    if (inactiveDuration > REMEMBER_ME_TIMEOUT) {
      return { valid: false, reason: 'inactivity_timeout' };
    }
  } else {
    // For regular sessions, use the old logic
    const sessionDuration = now - parseInt(sessionStart);

    // Check if session has exceeded maximum duration
    if (sessionDuration > MAX_SESSION_DURATION) {
      return { valid: false, reason: 'session_expired' };
    }

    // Check if user has been inactive too long
    if (inactiveDuration > SESSION_TIMEOUT) {
      return { valid: false, reason: 'inactivity_timeout' };
    }
  }

  return { valid: true, reason: 'active' };
};

/**
 * Initialize session monitoring
 * @param {Function} onSessionInvalid - Callback when session becomes invalid
 */
export const initSessionMonitoring = (onSessionInvalid) => {
  // Track user activity
  const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

  const handleActivity = () => {
    updateActivity();
  };

  activityEvents.forEach((event) => {
    window.addEventListener(event, handleActivity, { passive: true });
  });

  // Check session validity every minute
  const sessionCheckInterval = setInterval(() => {
    const sessionCheck = checkSession();

    if (!sessionCheck.valid) {
      console.warn('Session invalid:', sessionCheck.reason);
      clearInterval(sessionCheckInterval);

      // Remove event listeners
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      if (onSessionInvalid) {
        onSessionInvalid(sessionCheck.reason);
      }
    }
  }, 60000); // Check every minute

  // Return cleanup function
  return () => {
    clearInterval(sessionCheckInterval);
    activityEvents.forEach((event) => {
      window.removeEventListener(event, handleActivity);
    });
  };
};

/**
 * Clear session data
 */
export const clearSession = () => {
  // Clear from both storages
  sessionStorage.removeItem('session_start');
  sessionStorage.removeItem('last_activity');
  sessionStorage.clear();

  localStorage.removeItem('session_start');
  localStorage.removeItem('last_activity');
  localStorage.removeItem('remember_me');
  localStorage.removeItem('keycloak_tokens');
  localStorage.removeItem('session_expiry');
};

/**
 * Get session info
 * @returns {Object} Session information
 */
export const getSessionInfo = () => {
  const sessionStart = sessionStorage.getItem('session_start');
  const lastActivity = sessionStorage.getItem('last_activity');

  if (!sessionStart || !lastActivity) {
    return null;
  }

  const now = Date.now();
  const sessionDuration = now - parseInt(sessionStart);
  const inactiveDuration = now - parseInt(lastActivity);

  return {
    sessionStart: parseInt(sessionStart),
    lastActivity: parseInt(lastActivity),
    sessionDuration,
    inactiveDuration,
    timeUntilTimeout: SESSION_TIMEOUT - inactiveDuration,
    timeUntilExpiry: MAX_SESSION_DURATION - sessionDuration,
  };
};
